'use strict'
import React, { useMemo } from 'react'
import {
  makeStyles,
  Paper,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']

const useStyles = makeStyles(theme => ({
  weekPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  table: {
    marginTop: theme.spacing(1)
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    padding: '6px 10px',
    fontSize: '0.78rem'
  },
  cell: {
    padding: '5px 10px',
    fontSize: '0.78rem'
  },
  summaryRow: {
    backgroundColor: theme.palette.grey[50],
    '& td': {
      fontWeight: 600
    }
  },
  increased: {
    color: '#c62828'
  },
  decreased: {
    color: '#2e7d32'
  },
  neutral: {
    color: 'inherit'
  },
  snapshotChip: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600
  },
  estimatedChip: {
    fontWeight: 500
  }
}))

function DeltaCell ({ scenario, baseline, classes }) {
  const delta = scenario - baseline
  const cls = delta > 0 ? classes.increased : delta < 0 ? classes.decreased : classes.neutral
  const prefix = delta > 0 ? '+' : ''
  return (
    <span>
      {Math.round(scenario)}/{Math.round(baseline)}
      {' '}
      <span className={cls} style={{ fontSize: '0.7rem' }}>({prefix}{Math.round(delta)})</span>
    </span>
  )
}

function WeekTable ({ label, week, classes }) {
  const { dates = [], scenario = [], baseline = [] } = week || {}

  const summaryRow = useMemo(() => {
    const sum = (arr, key) => arr.reduce((t, d) => t + (Number(d[key]) || 0), 0)
    const sStops = sum(scenario, 'stops')
    const bStops = sum(baseline, 'stops')
    const sService = sum(scenario, 'serviceMin')
    const bService = sum(baseline, 'serviceMin')
    const sActualService = sum(scenario, 'actualServiceMin') || sService
    const bActualService = sum(baseline, 'actualServiceMin') || bService
    const sDrive = sum(scenario, 'driveMin')
    const bDrive = sum(baseline, 'driveMin')
    const sTotal = sum(scenario, 'totalMin')
    const bTotal = sum(baseline, 'totalMin')
    const sOT = sum(scenario, 'otMin')
    const bOT = sum(baseline, 'otMin')
    return { sStops, bStops, sService, bService, sActualService, bActualService, sDrive, bDrive, sTotal, bTotal, sOT, bOT }
  }, [scenario, baseline])

  const pctChange = (s, b) => {
    if (!b) return s > 0 ? '+100%' : '–'
    const pct = ((s - b) / b) * 100
    return `${pct > 0 ? '+' : ''}${Math.round(pct)}%`
  }

  return (
    <Paper variant='outlined' className={classes.weekPaper}>
      <Typography variant='subtitle2' style={{ fontWeight: 600, marginBottom: 4 }}>{label}</Typography>
      <Table className={classes.table} size='small'>
        <TableHead>
          <TableRow>
            <TableCell className={classes.headerCell}>Day</TableCell>
            <TableCell className={classes.headerCell}>Stops (S/B)</TableCell>
            <TableCell className={classes.headerCell}>Service (S/B)</TableCell>
            <TableCell className={classes.headerCell}>Actual Svc (S/B)</TableCell>
            <TableCell className={classes.headerCell}>Drive (S/B)</TableCell>
            <TableCell className={classes.headerCell}>Total (S/B)</TableCell>
            <TableCell className={classes.headerCell}>OT (S/B)</TableCell>
            <TableCell className={classes.headerCell}>Source</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dates.map((dateStr, i) => {
            const s = scenario[i] || {}
            const b = baseline[i] || {}
            const dayName = DAY_NAMES[i] || ''
            const source = b.source || 'estimated'
            return (
              <TableRow key={dateStr}>
                <TableCell className={classes.cell}>{dayName} {dateStr.slice(5)}</TableCell>
                <TableCell className={classes.cell}>
                  <DeltaCell scenario={s.stops || 0} baseline={b.stops || 0} classes={classes} />
                </TableCell>
                <TableCell className={classes.cell}>
                  <DeltaCell scenario={s.serviceMin || 0} baseline={b.serviceMin || 0} classes={classes} />
                </TableCell>
                <TableCell className={classes.cell}>
                  <DeltaCell scenario={s.actualServiceMin || s.serviceMin || 0} baseline={b.actualServiceMin || b.serviceMin || 0} classes={classes} />
                </TableCell>
                <TableCell className={classes.cell}>
                  <DeltaCell scenario={s.driveMin || 0} baseline={b.driveMin || 0} classes={classes} />
                </TableCell>
                <TableCell className={classes.cell}>
                  <DeltaCell scenario={s.totalMin || 0} baseline={b.totalMin || 0} classes={classes} />
                </TableCell>
                <TableCell className={classes.cell}>
                  <DeltaCell scenario={s.otMin || 0} baseline={b.otMin || 0} classes={classes} />
                </TableCell>
                <TableCell className={classes.cell}>
                  {source === 'snapshot'
                    ? <Chip size='small' label='snapshot' className={classes.snapshotChip} />
                    : <Chip size='small' label='estimated' variant='outlined' className={classes.estimatedChip} />
                  }
                </TableCell>
              </TableRow>
            )
          })}
          {/* Summary row */}
          <TableRow className={classes.summaryRow}>
            <TableCell className={classes.cell}>Total</TableCell>
            <TableCell className={classes.cell}>
              {Math.round(summaryRow.sStops)}/{Math.round(summaryRow.bStops)}
              {' '}<span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sStops, summaryRow.bStops)})</span>
            </TableCell>
            <TableCell className={classes.cell}>
              {Math.round(summaryRow.sService)}/{Math.round(summaryRow.bService)}
              {' '}<span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sService, summaryRow.bService)})</span>
            </TableCell>
            <TableCell className={classes.cell}>
              {Math.round(summaryRow.sActualService)}/{Math.round(summaryRow.bActualService)}
              {' '}<span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sActualService, summaryRow.bActualService)})</span>
            </TableCell>
            <TableCell className={classes.cell}>
              {Math.round(summaryRow.sDrive)}/{Math.round(summaryRow.bDrive)}
              {' '}<span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sDrive, summaryRow.bDrive)})</span>
            </TableCell>
            <TableCell className={classes.cell}>
              {Math.round(summaryRow.sTotal)}/{Math.round(summaryRow.bTotal)}
              {' '}<span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sTotal, summaryRow.bTotal)})</span>
            </TableCell>
            <TableCell className={classes.cell}>
              {Math.round(summaryRow.sOT)}/{Math.round(summaryRow.bOT)}
              {' '}<span style={{ fontSize: '0.7rem' }}>({pctChange(summaryRow.sOT, summaryRow.bOT)})</span>
            </TableCell>
            <TableCell className={classes.cell} />
          </TableRow>
        </TableBody>
      </Table>
    </Paper>
  )
}

export default function TwoWeekView ({ comparison }) {
  const classes = useStyles()
  if (!comparison || comparison.level !== 'two-week') return null

  return (
    <Box>
      <Typography variant='subtitle1' style={{ fontWeight: 600, marginBottom: 8 }}>
        Two-Week View — {comparison.crewName || comparison.crewId}
        <Typography variant='caption' color='textSecondary' style={{ marginLeft: 8 }}>
          Starting {comparison.weekOf}
        </Typography>
      </Typography>
      <WeekTable label='A-Week' week={comparison.aWeek} classes={classes} />
      <WeekTable label='B-Week' week={comparison.bWeek} classes={classes} />
    </Box>
  )
}
